/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { BEM, T } from "@adapptio/lib-components";
import { useStateWithEvent } from "../../../../../ui/hooks";
import { Panel, PanelNote } from "../../../../../ui/components/Panel";
import { ThemeEditorInspectorItem } from "./ThemeEditorInspectorItem";
import { termsEditor } from "../../../../../terms";
import { ThemeModel } from "../../../Common/ThemeModel";

export interface IThemeEditorInspectorProps {
	className?: string;
	themeModel: ThemeModel;
}

export const ThemeEditorInspector: React.FC<IThemeEditorInspectorProps> = ({ className, themeModel }) => {
	const baseClass = BEM("edt-theme-editor-inspector");

	const selection = useMemo(() => themeModel.getSelection(), [ themeModel ]);

	const getNodeById = useCallback(
		(nodeId: number) => themeModel.getDesignContext().getNodeById(nodeId),
		[ themeModel ]
	);

	const selectedItem = useStateWithEvent(selection.onChange, () => selection.getItems()[0]);

	if (className) {
		baseClass.add(className);
	}

	return (
		<Panel scroll mark="theme-inspector">
			{selectedItem ? (
				<ThemeEditorInspectorItem selectionItem={selectedItem} getNodeById={getNodeById} />
			) : (
				<PanelNote>
					<p>
						<T domain="editor" term={termsEditor.themeEditor.inspector.selectBlockToInspect} />
					</p>
				</PanelNote>
			)}
		</Panel>
	);
};
